import { ReactNode } from "react";
import { anxiousGenerationContent } from "./work-content/anxious-generation";
import { childhoodIndexContent } from "./work-content/childhood-index";
import { covidSchoolHubContent } from "./work-content/covid-school-hub";
import { fabrikContent } from "./work-content/fabrik";
import { feastAndFettleContent } from "./work-content/feast-and-fettle";
import { goodInsideContent } from "./work-content/good-inside";
import { safdieArchitectsContent } from "./work-content/safdie-architects";
import { exploContent } from "./work-content/explo";
import { leglerBenboughContent } from "./work-content/legler-benbough";
import { skybitzContent } from "./work-content/skybitz";
import type { WorkPortfolioTier, WorkPublicationStatus } from "./work-publication";
import { resolveWorkPublicationStatus } from "./work-publication";
import type { WorkMedia, WorkMediaAsset } from "./work-media";
import { workVideoPreviewPath } from "./work-media";

export interface WorkItem {
  id: string;
  slug: string | null;
  title: string;
  /** Short plain-text blurb for lists, API `summary`, and cards when space is tight. */
  description: string | ReactNode;
  /** Featured card / deck copy — string or paragraphs. Primary long-form pitch. */
  spotlightCopy?: string | string[];
  /** @deprecated Use `spotlightCopy`. Still accepted in source and mirrored on the API. */
  oneLiner?: string | string[];
  featuredMedia: WorkMedia;
  screenshot?: WorkMedia;
  /** Extra portfolio images/videos keyed for API consumers (e.g. deck variants). */
  mediaAssets?: WorkMediaAsset[];
  /** When set, deck views and API consumers use this `mediaAssets` key instead of `featuredMedia`. */
  deckMediaKey?: string;
  color: string;
  client: {
    name: string;
    contact?: string;
    logo: string;
    website?: string;
  };
  content?: ReactNode;
  listed?: boolean;
  /** Not on /work; may still appear on `/lp/*` campaign landings when true. */
  campaignLpOnly?: boolean;
  publicationStatus?: WorkPublicationStatus;
  /** `case-study` = real work; `architectural` = LP/API placeholder shell. */
  portfolioTier?: WorkPortfolioTier;
  longform?: boolean;
  quote?: {
    text: string;
    author?: string;
    role?: string;
  };
  categories?: string[];
}

/** Public work index and case study routes (`/work`). */
export function workItemOnPublicSite(item: WorkItem): boolean {
  return resolveWorkPublicationStatus(item) === "public";
}

/** Campaign LPs may show listed work plus `campaignLpOnly` portfolio items. */
export function workItemEligibleForCampaignLp(item: WorkItem): boolean {
  return workItemOnPublicSite(item) || item.campaignLpOnly === true;
}

export const WORK_ITEMS: WorkItem[] = [
  {
    id: "anxious-generation",
    slug: "anxious-generation",
    listed: true,
    longform: true,
    title: "The Anxious Generation",
    spotlightCopy: [
      "We collaborated with NYT best-selling author, Jonathan Haidt, to create a website that not only promotes his book 'The Anxious Generation' but also serves as the foundation for the movement to restore play-based childhoods. The site has helped pass phone-free school policies in multiple states and continues to support the growing movement.",
      "Beyond book promotion, it provides resources for parents, educators, and policymakers, connects communities working toward phone-free schools, and amplifies the conversation about protecting childhood in the digital age.",
    ],
    description: (
      <p>
        We&rsquo; ve collaborated with NYT best-selling author, Jonathan Haidt,
        to create a marketing website that not only promotes his book &lsquo; The Anxious
        Generation&rsquo; but also serves as the foundation for the movement to
        restore play-based childhoods.
      </p>
    ),
    featuredMedia: {
      type: "video",
      url: "/assets/work/tag-trim.mp4",
    },
    color: "#F1C402",
    client: {
      name: "Jonathan Haidt",
      logo: "/assets/logos/jonh.webp",
      website: "https://theanxiousgeneration.com",
    },
    content: anxiousGenerationContent,
    categories: ["digital-experiences", "marketing-websites"],
  },
  {
    id: "childhood-index",
    slug: "childhood-index",
    listed: true,
    longform: true,
    title: "The Childhood Index",
    spotlightCopy: [
      "What began as a simple policy map on The Anxious Generation website evolved into the Childhood Index, a dedicated microsite built to inspire action. Working closely with The Anxious Generation Movement and their policy team, Township helped transform the concept into a tool that tracks how states are advancing policies around phone-free schools, childhood independence, and protecting kids online.",
      "Built as a living resource, the Childhood Index ranks all 50 states and evolves alongside new legislation, helping policymakers, advocates, and parents understand where progress is happening and where more work remains.",
    ],
    description: (
      <p>
        Township has been part of The Anxious Generation Movement from day one.
        Together with their team, working closely with their policy team, we
        built the Childhood Index as a live marketing website: a transparent guide
        that ranks all 50 states on laws and leadership for phone-free schools,
        childhood independence, and tech regulation.
      </p>
    ),
    featuredMedia: {
      type: "video",
      url: "/assets/work/childhood-index-crop.mp4",
    },
    color: "#F1C402",
    client: {
      name: "The Anxious Generation Movement",
      logo: "/assets/logos/childhood-index-icon.png",
      website: "https://www.childhoodindex.org/",
    },
    content: childhoodIndexContent,
    categories: ["digital-experiences", "marketing-websites"],
  },
  {
    id: "safdie-architects",
    slug: "safdie-architects",
    listed: true,
    longform: true,
    title: "Safdie Architects",
    spotlightCopy:
      "Township partnered with Safdie Architects on the design and development of a new portfolio website for the internationally recognized architecture studio. The goal was to create a calm, confident digital experience that could support long-form project storytelling, thoughtful motion, and a growing body of work across geographies and disciplines. Built on a flexible publishing system, the site gives the studio a durable platform for showcasing projects with the same level of care and precision reflected in the architecture itself.",
    description:
      "Safdie Architects is world-renowned for civic and cultural work that defines skylines and public life. We designed and built a portfolio marketing website for their team to showcase that work online with the same clarity and craft as the projects themselves.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/safdie3.mp4",
    },
    color: "#1B2524",
    client: {
      name: "Safdie Architects",
      logo: "/assets/logos/safdie-architects.jpeg",
      website: "https://www.safdie.com",
    },
    content: safdieArchitectsContent,
    categories: [
      "digital-experiences",
      "marketing-websites",
      "real-estate-architecture",
    ],
  },
  {
    id: "barbara-bush-foundation",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    title: "Barbara Bush Foundation",
    description:
      "Township partnered with C/R Strategy to build out the V1 version of the Barbara Bush Foundation\u2019s Digital Learning Platform (DLP).",
    spotlightCopy:
      "This custom-built platform significantly enhanced program delivery by providing access to the Foundation\u2019s Book Explorers materials and facilitating mentoring sessions. Township\u2019s work took a proof of concept and, driven by user feedback from program advisors, mentors, mentees, and parents, built out an improved user experience to enhance both student engagement and the effectiveness for mentors and advisors.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/bbf.mp4",
    },
    screenshot: {
      type: "image",
      url: "/assets/work/bbf.png",
    },
    color: "#2C5282",
    client: {
      name: "Barbara Bush Foundation",
      logo: "/assets/logos/bb.webp",
      website: "https://www.barbarabush.org/",
    },
    content: (
      <>
        <p>
          This custom-built platform significantly enhanced program delivery by
          providing access to the Foundation&rsquo;s Book Explorers materials
          and facilitating mentoring sessions. Township&rsquo;s work took a
          proof of concept and, driven by user feedback from program advisors,
          mentors, mentees, and parents, built out an improved user experience
          to enhance both student engagement and the effectiveness for mentors
          and advisors.
        </p>
      </>
    ),
    categories: ["connected-systems", "product-design"],
  },
  {
    id: "fabrik",
    slug: "fabrik",
    listed: true,
    longform: true,
    title: "Fabrik",
    spotlightCopy: [
      "Fabrik is on a mission to take underutilized spaces and transform them into community spaces. Not just co-working \u2014 true third spaces available for anyone in the community to take advantage of.",
      "Township helped them build a powerful site using a modular, component-based architecture. Starting in Figma, we adopted an atomic design bottoms-up approach \u2014 flexible components that could be customized and combined to build pages.",
      "Fabrik\u2019s team is tech savvy and more than capable of handling site maintenance. They needed a site that was beautiful, built in a way that was flexible, that they could ultimately control.",
      "We worked with Fabrik to make some educated guesses, but made sure the site was flexible enough to test those hypotheses and adjust. Fabrik can quickly run tests on value propositions, customer segments, benefit statements, etc.",
    ],
    description:
      "Site design, site development, copywriting, and brand refresh for Fabrik\u2019s marketing website and modular design system.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/fabrik.mp4",
    },
    color: "#043839",
    client: {
      name: "Fabrik",
      logo: "/assets/logos/fabrik.png",
      website: "https://joinfabrik.com",
    },
    content: fabrikContent,
    quote: {
      text: "Working with Township has been a gamechanger for us. They have a knack for understanding and amplifying our mission, helping us tell our story more effectively through both our website and mobile app. For us, they weren't just an agency, they were a strategic partner that helped us get our brand and product off the ground.",
      author: "Nishi Patel Murphy",
      role: "Head of Product, Fabrik",
    },
    categories: ["digital-experiences", "marketing-websites"],
  },
  {
    id: "covid-school-hub",
    slug: "covid-school-hub",
    listed: true,
    longform: true,
    title: "COVID School Hub",
    spotlightCopy:
      "To better understand the impacts of pandemic school closures, Dr. Emily Oster and her team began to gather data about educational modes employed across the country. Township helped transform that data into a comprehensive dashboard that provided the public with digestible insights on the intersection of COVID-19, education, and health.",
    description:
      "Resource website helping schools navigate COVID-19 reopening challenges, built with Emily Oster.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/covidhub-small.mp4",
    },
    color: "#1C37A3",
    client: {
      name: "Emily Oster",
      logo: "/assets/logos/oster.webp",
      website: "https://covidschoolhub.org",
    },
    content: covidSchoolHubContent,
    quote: {
      text: "Township has been fantastic to work with; they are responsive and capable problem solvers, and willing to adapt as the project has adapted.",
      author: "Dr. Emily Oster",
      role: "Author and Professor of Economics at Brown University",
    },
    categories: ["digital-experiences", "marketing-websites"],
  },
  {
    id: "good-inside",
    slug: "good-inside",
    listed: true,
    longform: true,
    title: "Good Inside",
    spotlightCopy: [
      "Good Inside, Dr. Becky Kennedy\u2019s parenting platform, partnered with Township to support their web team in designing and optimizing paid campaigns and landing pages.",
      "Township has worked with the Good Inside team to update brand standards, design new pages and interfaces for new integrations, and refine existing campaigns to boost Good Inside subscribers.",
      "A recent highlight was the launch page for Dr. Becky\u2019s debut children\u2019s book, That\u2019s My Truck! Township designed and built the page; Dr. Becky shared it with her audience on Instagram the day it went live.",
    ],
    description:
      "Campaign landing pages, brand standards, and conversion-focused page design for Dr. Becky Kennedy\u2019s parenting platform.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/gi-kids.mp4",
    },
    screenshot: {
      type: "image",
      url: "/assets/work/good-inside-praise.png",
    },
    color: "#F1C402",
    client: {
      name: "Dr. Becky Kennedy",
      logo: "/assets/logos/becky.jpg",
      website: "https://goodinside.com",
    },
    content: goodInsideContent,
    quote: {
      text: "As you may know, I recently announced that I've written my first children's book (!) My incredible team partnered with Township to create - dare I say - the most beautiful web page... and it's now live on our site. Yes, I might be a little biased... but see for yourself!",
      author: "Dr. Becky Kennedy",
      role: "Founder, Good Inside",
    },
    categories: ["digital-experiences", "marketing-websites"],
  },
  {
    id: "alcor-ised",
    slug: "alcor-mini-ised",
    listed: true,
    title: "Alcor mini iSED",
    spotlightCopy: [
      "In late 2018 Alcor asked us to lead the development of a novel new hardware and software architecture for a new lab device they were bringing to market, the mini iSED. This new architecture would move much of the smarts to a Linux based System-on-Module chip, allowing for the use of modern software to drive much of the robotics orchestration and user interface.",
      "Launching in Q1 2020, the mini iSED transformed Alcor\u2019s business, and this new architecture became the foundation for all of their lab devices.",
    ],
    description:
      "In late 2018 Alcor asked us to lead the development of a novel new hardware and software architecture for a new lab device they were bringing to market, the mini iSED. This new architecture would move much of the smarts to a Linux based System-on-Module chip, allowing for the use of modern software to drive much of the robotics orchestration and user interface. Launching in Q1 2020, the mini iSED transformed Alcor\u2019s business, and this new architecture became the foundation for all of their lab devices.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/alcor-mini.mp4",
    },
    color: "#71D9C5",
    client: {
      name: "Alcor",
      logo: "/assets/logos/alc.png",
      website: "https://alcor.health",
    },
    categories: ["medical-devices", "connected-devices"],
  },
  {
    id: "alcor-pro",
    slug: "alcor-ised-pro",
    listed: true,
    title: "Alcor iSED Pro",
    description:
      "In 2024, after porting the architecture and software we built for the mini iSED to their full existing product portfolio, Alcor invited us back to lead development of their new Pro - a high volume ESR device. We led development of all software and UI elements, exercising granular control over the Robot, analytical system, and rack.",
    spotlightCopy:
      "In 2024, after porting the architecture and software we built for the mini iSED to their full existing product portfolio, Alcor invited us back to lead development of their new Pro - a high volume ESR device. We led development of all software and UI elements, exercising granular control over the Robot, analytical system, and rack.",
    featuredMedia: {
      type: "image",
      url: "/assets/work/alcor-pro.png",
    },
    mediaAssets: [
      {
        key: "deck-feature",
        label: "Device and UI",
        media: {
          type: "image",
          url: "/assets/work/alcor-pro-deck.png",
        },
      },
    ],
    deckMediaKey: "deck-feature",
    color: "#71D9C5",
    client: {
      name: "Alcor",
      logo: "/assets/logos/alc.png",
      website: "https://alcor.health",
    },
    categories: ["medical-devices", "connected-devices"],
  },
  {
    id: "chargerhelp-empwr-mobile",
    slug: "chargerhelp-empwr-ai",
    listed: true,
    title: "ChargerHelp EMPWR AI",
    spotlightCopy: [
      "Township partnered with ChargerHelp to overhaul their EV data platform into a user-friendly AI-powered hub that supports customers\u2019 and administrators\u2019 complex maintenance flows.",
      "We built and demoed prototypes to collect feedback and insights from the platform\u2019s core users, then partnered closely with ChargerHelp\u2019s product team to design and build new features.",
      "The LLM implementation connected across communication platforms, quickly pulling key information from customer support tickets and emails to surface relevant work orders and historical data, provide a holistic view of asset health and work in flight, and aid predictive analysis for future charger health.",
    ],
    description:
      "Township partnered with ChargerHelp to overhaul their EV data platform into a user-friendly AI-powered hub that supports customers\u2019 and administrators\u2019 complex maintenance flows.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/chargerhelp_ai_march25_no_audio_2.mp4",
    },
    color: "#71D9C5",
    client: {
      name: "ChargerHelp",
      logo: "/assets/logos/ch.svg",
      website: "https://chargerhelp.com",
    },
    content: (
      <>
        <p>
          We built and demoed prototypes to collect feedback and insights from
          the platform&rsquo;s core users, then partnered closely with
          ChargerHelp&rsquo;s product team to design and build new features.
        </p>
        <p>
          The LLM implementation connected across communication platforms,
          quickly pulling key information from customer support tickets and
          emails to surface relevant work orders and historical data, provide a
          holistic view of asset health and work in flight, and aid predictive
          analysis for future charger health.
        </p>
      </>
    ),
    categories: ["ai-consulting", "connected-systems"],
  },
  {
    id: "fluidlogic",
    slug: null,
    listed: true,
    title: "FluidLogic",
    description:
      "Mobile app for FluidLogic\u2019s Intelligent Hydration system, recording every sip and ride to build a personal hydration profile for endurance athletes and off-road racers.",
    spotlightCopy:
      "Township partnered with FluidLogic to build the app, helping riders record workouts and adventures, evaluate hydration performance, and build a personal profile tied to FluidLogic\u2019s wearable system.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/fl-homepage-video-v2.mp4",
    },
    color: "#71D9C5",
    client: {
      name: "FluidLogic",
      logo: "/assets/logos/fluidlogic_usa_logo.jpeg",
      website: "https://fluidlogic.com/pages/wearable-system",
    },
    content: (
      <>
        <p>
          Township partnered with FluidLogic to build the app, helping riders
          record workouts and adventures, evaluate hydration performance, and
          build a personal profile tied to FluidLogic&rsquo;s wearable system.
        </p>
      </>
    ),
    categories: ["connected-systems", "connected-devices", "mobile-apps"],
  },
  {
    id: "amulet-scientific",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    title: "Amulet",
    description:
      "The Amulet is a portable potentiostat. Township built an app as the primary interface for the Amulet — allowing users to define test parameters, observe test behavior, see immediate test outputs for field analysis, and easy exporting for review back at the lab.",
    spotlightCopy:
      "The Amulet is a portable potentiostat. Township built an app as the primary interface for the Amulet — allowing users to define test parameters, observe test behavior, see immediate test outputs for field analysis, and easy exporting for review back at the lab.",
    featuredMedia: {
      type: "image",
      url: "/assets/work/amulet.png",
    },
    color: "#5B4E96",
    client: {
      name: "Amulet Scientific",
      logo: "/assets/logos/amulet.png",
      website: "https://www.amuletscientific.com",
    },
    content: (
      <>
        <p>
          The Amulet is a portable potentiostat. Township built an app as the
          primary interface for the Amulet — allowing users to define test
          parameters, observe test behavior, see immediate test outputs for
          field analysis, and easy exporting for review back at the lab.
        </p>
      </>
    ),
    categories: ["medical-devices", "connected-devices", "mobile-apps"],
  },
  {
    id: "feast-and-fettle",
    slug: "feast-and-fettle",
    listed: true,
    longform: true,
    title: "Feast & Fettle",
    description:
      "From startup to scale-up: Building a custom e-commerce platform to support rapid growth and expansion.",
    spotlightCopy: [
      "Feast & Fettle has been a steady shooting star since launching in 2016. When we started working with them in 2017, Feast & Fettle were using an out-of-the-box e-commerce platform that was limiting their long-term growth potential.",
      "Over five years of continuous collaboration, we worked with Feast & Fettle to maximize results with the low-code platform before transitioning them to a powerful, fully custom web app designed and built in close collaboration with their in-house team.",
    ],
    featuredMedia: {
      type: "image",
      url: "/assets/work/ff.webp",
    },
    color: "#FF7A45",
    client: {
      name: "Feast & Fettle",
      logo: "/assets/logos/ff.webp",
      website: "https://feastandfettle.com",
    },
    content: feastAndFettleContent,
    quote: {
      text: "We were incredibly lucky to have already built the infrastructure for scale before the pandemic hit.... Our team was able to successfully manage demand without ever sacrificing on product quality.",
      author: "Carlos Ventura",
      role: "Feast & Fettle CEO",
    },
    categories: ["connected-systems"],
  },
  {
    id: "splitwise",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    title: "Splitwise Brand",
    description:
      "Reimagining the visual identity and user interface for a popular expense-sharing platform.",
    spotlightCopy:
      "Splitwise came to Township in search of an updated Visual Identity and User Interface in preparation for a major rebrand of their popular mobile app. We worked closely with the Splitwise team to bring this new identity to life in their flagship application and supporting sub-brands like Splitwise Pay.",
    featuredMedia: {
      type: "image",
      url: "/assets/case-studies/splitwise-pay-hero.png",
    },
    mediaAssets: [
      {
        key: "logo-comparison",
        label: "Logo before / after",
        media: {
          type: "image",
          url: "/assets/case-studies/splitwise-pay-logo-comparison.png",
        },
      },
      {
        key: "brand-before-after",
        label: "Brand before / after",
        media: {
          type: "image",
          url: "/assets/case-studies/splitwise-brand-before-after.png",
        },
      },
      {
        key: "brand-illustrations",
        label: "Brand illustrations and Splitwise Pay",
        media: {
          type: "image",
          url: "/assets/case-studies/splitwise-brand-illustrations.png",
        },
      },
    ],
    deckMediaKey: "brand-illustrations",
    color: "#1CC29F",
    client: {
      name: "Splitwise",
      logo: "/assets/logos/splitwise.png",
      website: "https://splitwise.com",
    },
    content: (
      <>
        <p>
          Splitwise came to Township in search of an updated Visual Identity and
          User Interface in preparation for a major rebrand of their popular
          mobile app. We worked closely with the Splitwise team to bring this
          new identity to life in their flagship application and supporting
          sub-brands like Splitwise Pay.
        </p>
      </>
    ),
    categories: ["digital-experiences", "product-design", "mobile-apps"],
  },
  {
    id: "moneta",
    slug: null,
    listed: true,
    title: "Moneta",
    description:
      "Township partnered with Moneta to design their brand from the ground up. Moneta is a telephone-based cognitive rehab program aimed at battling dementia and improving cognitive function.",
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("moneta"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/moneta.png",
    },
    color: "#1D2932",
    client: {
      name: "Moneta",
      logo: "/assets/logos/moneta.png",
      website: "https://moneta.health",
    },
    content: (
      <>
        <p>
          Together, we&rsquo; ve worked with Moneta to shape their marketing and
          pitch materials, as well as to help define the future vision of their
          AI product, Mona.
        </p>
      </>
    ),
    categories: ["digital-experiences"],
  },
  {
    id: "legler-benbough-foundation",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    title: "Legler Benbough Foundation",
    description:
      "Philanthropic marketing website capturing 20 years of Legler Benbough Foundation impact through an interactive digital experience.",
    spotlightCopy:
      "The Legler Benbough Foundation approached Township to help document and share their unique approach to \u201cGrantee Relationship Philanthropy\u201d developed over 20 years of community impact. We created an interactive digital experience that makes their lessons learned accessible to other foundations and philanthropic organizations.",
    featuredMedia: {
      type: "video",
      url: "/assets/work/benbough.mp4",
    },
    color: "#4b66af",
    client: {
      name: "Legler Benbough Foundation",
      logo: "/assets/logos/lbf.png",
      website: "https://benboughlegacy.org",
    },
    content: leglerBenboughContent,
    categories: ["digital-experiences", "marketing-websites"],
  },
  {
    id: "crain",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    title: "Crain Design System",
    description:
      "Robust cross-brand design system for nearly 30 news properties, balancing shared components with brand-level customization and expression.",
    spotlightCopy: [
      "Crain engaged with Township to build out a robust design system that would work across the entire Crain portfolio of nearly 30 distinct news brands. Starting with the design of universal components and page templates that would populate each site, we worked with Crain brand leaders to bring customization and brand expression to the design system so each site would still feel distinct and meet each brand\u2019s specific content needs.",
      "Leveraging the power of configurable component variants in Figma and the multi-theme capabilities of the design system tool Tokens Studio, our design system allowed for easy translation to Crain\u2019s engineering team, who used the building blocks we designed to replatform the entire Crain ecosystem to a new CMS.",
    ],
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("crain"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/crain.png",
    },
    color: "#FFE28A",
    client: {
      name: "Crain",
      logo: "/assets/logos/crain_logo.jpeg",
      website: "https://crain.design",
    },
    content: (
      <>
        <p>
          Crain engaged with Township to build out a robust design system that would
          work across the entire Crain portfolio of nearly 30 distinct news
          brands. Starting with the design of universal components and page
          templates that would populate each site, we worked with Crain brand
          leaders to bring customization and brand expression to the design
          system so each site would still feel distinct and meet each
          brand&rsquo;s specific content needs.
        </p>
        <p>
          Leveraging the power of configurable component variants in Figma and
          the multi-theme capabilities of the design system tool Tokens Studio,
          our design system allowed for easy translation to Crain&rsquo;s
          engineering team, who used the building blocks we designed to
          replatform the entire Crain ecosystem to a new CMS.
        </p>
      </>
    ),
    categories: ["digital-experiences", "product-design"],
  },
  {
    id: "docent-collection",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "The Docent\u2019s Collection",
    description:
      "Marketing website for The Docent\u2019s Collection — boutique hospitality across Portland\u2019s Old Port.",
    spotlightCopy:
      "Township partnered with Longfellow Communications on the launch of a refreshed website for The Docent\u2019s Collection for East Brown Cow and Commonwealth Hotels. The hospitality concept blends boutique hotel experiences across a collection of distinctive properties in Portland\u2019s Old Port district. Township designed and developed a clean digital presence for the brand, translating its editorial tone, flexible accommodations, and neighborhood-focused approach into a platform built to support discovery, booking, and future growth.",
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("docents-collection"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/docents-collection.png",
    },
    color: "#8B7355",
    client: {
      name: "The Docent\u2019s Collection",
      logo: "/assets/logos/docents-collection.png",
      website: "https://www.thedocentscollection.com",
    },
    content: (
      <>
        <p>
          Township partnered with Longfellow Communications on the launch of a
          refreshed website for The Docent&rsquo;s Collection for East Brown Cow
          and Commonwealth Hotels. The hospitality concept blends boutique hotel
          experiences across a collection of distinctive properties in
          Portland&rsquo;s Old Port district. Township designed and developed a
          clean digital presence for the brand, translating its editorial tone,
          flexible accommodations, and neighborhood-focused approach into a
          platform built to support discovery, booking, and future growth.
        </p>
      </>
    ),
    categories: [
      "digital-experiences",
      "marketing-websites",
      "real-estate-architecture",
    ],
  },
  {
    id: "core-firms",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "CORE",
    description:
      "Marketing website for CORE — a commercial real estate firm serving Northern New England.",
    spotlightCopy:
      "Township partnered with Longfellow Communications on the design and development of the website for CORE, a commercial real estate firm serving Northern New England. Working from the brand and strategic direction established by Longfellow, Township designed and built a modern website experience focused on property discovery, market storytelling, and showcasing CORE\u2019s integrated approach across brokerage, management, acquisitions, and development services.",
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("core-firms"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/core-firms.png",
    },
    color: "#1E3A5F",
    client: {
      name: "CORE",
      logo: "/assets/logos/core-firms.png",
      website: "https://www.corefirms.com",
    },
    content: (
      <>
        <p>
          Township partnered with Longfellow Communications on the design and
          development of the website for CORE, a commercial real estate firm
          serving Northern New England. Working from the brand and strategic
          direction established by Longfellow, Township designed and built a
          modern website experience focused on property discovery, market
          storytelling, and showcasing CORE&rsquo;s integrated approach across
          brokerage, management, acquisitions, and development services.
        </p>
      </>
    ),
    categories: [
      "digital-experiences",
      "marketing-websites",
      "real-estate-architecture",
    ],
  },
  {
    id: "old-port-square",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "Old Port Square",
    description:
      "Marketing website for Old Port Square — Portland waterfront property with leasing storytelling, maps, and neighborhood context.",
    spotlightCopy:
      "Township partnered with Pentagram, Longfellow Communications, and East Brown Cow to help bring Old Port Square to life: a major revitalization effort in the heart of Portland, Maine\u2019s historic Old Port district. Working alongside the broader vision led by Safdie Architects and architect Moshe Safdie, Township helped translate the evolving brand and district identity into an interactive digital experience, designed to showcase the development\u2019s architecture, public spaces, hospitality, and long-term vision for the neighborhood.",
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("old-port-square"),
    },
    screenshot: {
      type: "image",
      url: "/assets/logos/old-port-square.jpg",
    },
    color: "#FF8E72",
    client: {
      name: "Old Port Square",
      logo: "/assets/logos/old-port-square.png",
      website: "https://www.oldportsquare.com",
    },
    content: (
      <>
        <p>
          Township partnered with Pentagram, Longfellow Communications, and East
          Brown Cow to help bring Old Port Square to life: a major revitalization
          effort in the heart of Portland, Maine&rsquo;s historic Old Port
          district. Working alongside the broader vision led by Safdie Architects
          and architect Moshe Safdie, Township helped translate the evolving
          brand and district identity into an interactive digital experience,
          designed to showcase the development&rsquo;s architecture, public
          spaces, hospitality, and long-term vision for the neighborhood.
        </p>
      </>
    ),
    categories: [
      "digital-experiences",
      "marketing-websites",
      "real-estate-architecture",
    ],
  },
  {
    id: "insi",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    title: "INSI",
    description:
      "Township partnered with C/R Strategy Partners to develop INSI, an AI-enabled platform that helps leaders and teams understand how they truly operate in innovation environments.",
    spotlightCopy: [
      "Township partnered with C/R Strategy Partners to develop INSI, an AI-enabled platform that helps leaders and teams understand how they truly operate in innovation environments.",
      "Built around C/R\u2019s proprietary INSI framework, the platform combines assessment data with large language models to generate personalized leadership readouts, uncover innovation strengths and blind spots, and provide actionable recommendations for growth.",
      "The platform extends beyond individual insights to deliver team-level analysis, highlighting alignment, capability gaps, complementary strengths, and opportunities for improvement. Over time, INSI tracks changes in performance, helping organizations measure progress and build more effective innovation cultures.",
    ],
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("insi"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/insi.png",
    },
    color: "#F6BBCD",
    client: {
      name: "C/R Strategy Partners",
      logo: "/assets/logos/insi.png",
      website: "https://www.crstrategypartners.com/insi",
    },
    content: (
      <>
        <p>
          Township partnered with C/R Strategy Partners to develop INSI, an
          AI-enabled platform that helps leaders and teams understand how they
          truly operate in innovation environments.
        </p>
        <p>
          Built around C/R&rsquo;s proprietary INSI framework, the platform
          combines assessment data with large language models to generate
          personalized leadership readouts, uncover innovation strengths and
          blind spots, and provide actionable recommendations for growth.
        </p>
        <p>
          The platform extends beyond individual insights to deliver team-level
          analysis, highlighting alignment, capability gaps, complementary
          strengths, and opportunities for improvement. Over time, INSI tracks
          changes in performance, helping organizations measure progress and
          build more effective innovation cultures.
        </p>
      </>
    ),
    categories: ["ai-consulting"],
  },
  {
    id: "osteoboost",
    slug: "osteoboost",
    listed: true,
    title: "Osteoboost",
    description:
      "Osteoboost built a wearable device to help men and women suffering from bone density loss. We worked with their team from concept to FDA approval through to launch, building an app that pairs seamlessly with their wearable device to engage users and improve adherence.",
    spotlightCopy:
      "Osteoboost built a wearable device to help men and women suffering from bone density loss. We worked with their team from concept to FDA approval through to launch, building an app that pairs seamlessly with their wearable device to engage users and improve adherence.",
    featuredMedia: {
      type: "image",
      url: "/assets/work/OST.png",
    },
    color: "#71D9C5",
    client: {
      name: "Osteoboost",
      logo: "/assets/logos/ost.jpeg",
      website: "https://osteoboost.com",
    },
    categories: ["medical-devices", "connected-devices", "mobile-apps"],
  },
  {
    id: "kwieri",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    portfolioTier: "architectural",
    title: "Kwieri",
    description:
      "Township partnered with Kwieri to design and ship a platform that blends AI-generated output with live expert mentorship — helping learners and teams understand their work, not just complete it.",
    spotlightCopy: [
      "Kwieri addresses a growing gap: AI that moves fast but produces the illusion of competence without real comprehension. Their patented platform blends generative AI with live expert mentorship so students, teams, and new hires don\u2019t just finish the task \u2014 they learn how to think through it.",
      "Township partnered with Kwieri to design and ship the product experience from early AI prototyping through production integration. Users start with AI-generated research and content, then collaborate in real time with matched mentors who add context, push better follow-up questions, and refine results through guided sessions at scale.",
      "The experience supports AI-only, AI + mentor, and mentor-only modes \u2014 scaling expert feedback across education, consulting, enterprise L&D, and more without sacrificing depth for speed.",
    ],
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("kwieri"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/kwieri.png",
    },
    color: "#7C9CFF",
    client: {
      name: "Kwieri",
      logo: "/assets/logos/kwieri.png",
      website: "https://kwieri.ai",
    },
    content: (
      <>
        <p>
          Kwieri addresses a growing gap: AI that moves fast but produces the
          illusion of competence without real comprehension. Their patented
          platform blends generative AI with live expert mentorship so students,
          teams, and new hires don&rsquo;t just finish the task — they learn how
          to think through it.
        </p>
        <p>
          Township partnered with Kwieri to design and ship the product
          experience from early AI prototyping through production integration.
          Users start with AI-generated research and content, then collaborate
          in real time with matched mentors who add context, push better
          follow-up questions, and refine results through guided sessions at
          scale.
        </p>
        <p>
          The experience supports AI-only, AI + mentor, and mentor-only modes —
          scaling expert feedback across education, consulting, enterprise
          L&amp;D, and more without sacrificing depth for speed.
        </p>
      </>
    ),
    categories: ["ai-consulting"],
  },
  {
    id: "moneta-voice-agent",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    portfolioTier: "case-study",
    title: "Moneta Voice Agent",
    description:
      "Township partnered with Moneta to shape Mona, a voice-first AI agent that guides patients through cognitive rehabilitation sessions by phone, with natural conversation, adaptive pacing, and clinical guardrails.",
    spotlightCopy: [
      "Township partnered with Moneta to shape Mona, a voice-first AI agent that guides patients through cognitive rehabilitation sessions by phone, with natural conversation, adaptive pacing, and clinical guardrails.",
      "Alongside the voice experience, we designed therapist tools for managing patients and building individualized activity plans, allowing clinicians to tailor sessions to each patient\u2019s interests, abilities, and treatment goals.",
      "The platform was intentionally built around clinician ownership. Therapy content, activities, and patient context can be continuously updated through a maintainable knowledge base, enabling Mona to evolve over time without requiring ongoing software development.",
    ],
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("moneta-voice-agent"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/moneta-voice-agent.png",
    },
    color: "#F6BBCD",
    client: {
      name: "Moneta",
      logo: "/assets/logos/moneta.png",
      website: "https://moneta.health",
    },
    content: (
      <>
        <p>
          Township partnered with Moneta to shape Mona, a voice-first AI agent
          that guides patients through cognitive rehabilitation sessions by
          phone, with natural conversation, adaptive pacing, and clinical
          guardrails.
        </p>
        <p>
          Alongside the voice experience, we designed therapist tools for
          managing patients and building individualized activity plans,
          allowing clinicians to tailor sessions to each patient&rsquo;s
          interests, abilities, and treatment goals.
        </p>
        <p>
          The platform was intentionally built around clinician ownership.
          Therapy content, activities, and patient context can be continuously
          updated through a maintainable knowledge base, enabling Mona to evolve
          over time without requiring ongoing software development.
        </p>
      </>
    ),
    categories: ["ai-consulting"],
  },
  {
    id: "splitwise-receipt-scanner",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    title: "Splitwise Receipt Scanner",
    description:
      "Splitwise partnered with Township to prototype an AI receipt scanner that goes beyond OCR, with conversational natural-language bill splitting.",
    spotlightCopy:
      "Splitwise partnered with Township to prototype an AI receipt scanner that goes beyond OCR. Users upload a photo, the app extracts line items and totals, then a conversational interface lets the group describe how to split the bill in natural language instead of manually assigning every item.",
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("splitwise-receipt-scanner"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/splitwise-receipt-scanner.png",
    },
    color: "#F6BBCD",
    client: {
      name: "Splitwise",
      logo: "/assets/logos/splitwise.png",
      website: "https://splitwise.com",
    },
    content: (
      <>
        <p>
          Splitwise partnered with Township to prototype an AI receipt scanner
          that goes beyond OCR. Users upload a photo, the app extracts line
          items and totals, then a conversational interface lets the group
          describe how to split the bill in natural language instead of
          manually assigning every item.
        </p>
      </>
    ),
    categories: ["ai-consulting"],
  },
  {
    id: "explo",
    slug: null,
    listed: true,
    title: "EXPLO",
    spotlightCopy:
      "EXPLO, an immersive summer program for middle schoolers, partnered with Township to fully redesign their onboarding process for kids and parents. When the process began, EXPLO relied on a series of third-party tools to manage the various forms, applications, waivers, and permissions required for every camper. Township was able to design a streamlined flow that transformed the application process into a user-friendly, single application.",
    description:
      "EXPLO, an immersive summer program for middle schoolers, partnered with Township to fully redesign their onboarding process for kids and parents.",
    featuredMedia: {
      type: "image",
      url: "/assets/work/explo.png",
    },
    color: "#FF7D00",
    client: {
      name: "EXPLO",
      logo: "/assets/logos/explo.jpg",
      website: "https://explo.org",
    },
    content: exploContent,
    categories: ["connected-systems", "product-design"],
  },
  {
    id: "skybitz",
    slug: "skybitz",
    listed: true,
    title: "SkyBitz SmartTank",
    spotlightCopy: [
      "Township elevated the product design, functionality, and UX of SmartTank\u2019s legacy admin portal, used to provide customers with real-time tank monitoring data.",
      "The improved portal now features BI Dashboards that offer quick, visual snapshots of low-level tanks, product demand, and overall delivery efficiency.",
      "Users can view, sort, and filter tanks by any parameter with an interactive display. The interface offers essential KPIs for monitoring activities, meeting targets, and maximizing savings. A customizable grid view also shows key tank metrics like current gallons, available capacity, volume percentage, temperatures, signal strength, and battery level.",
    ],
    description:
      "Township elevated the product design, functionality, and UX of SmartTank's legacy admin portal, used to provide customers with real-time tank monitoring data.",
    featuredMedia: {
      type: "image",
      url: "/assets/work/smarttank.png",
    },
    color: "#0088DD",
    client: {
      name: "SkyBitz",
      logo: "/assets/logos/sb.png",
    },
    content: skybitzContent,
    categories: ["connected-systems", "product-design"],
  },
  {
    id: "velcoti",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    title: "Velociti Command Center",
    description:
      "AI-driven prototyping platform that accelerates product development through intelligent design automation and rapid iteration.",
    featuredMedia: {
      type: "image",
      url: "/assets/case-studies/velociticom.png",
    },
    color: "#F6BBCD",
    client: {
      name: "Velociti Command Center",
      logo: "/assets/logos/velo.webp",
      website: "https://velcoti.ai",
    },
    content: (
      <>
        <p>
          Velociti Command Center partnered with Township to create an
          AI-powered prototyping platform that uses machine learning to automate
          design iterations, predict user needs, and accelerate product
          development cycles.
        </p>
      </>
    ),
    categories: ["coming-soon"],
  },
  {
    id: "skyverge-jilt",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "Jilt",
    spotlightCopy:
      "Product design and UX for Jilt — lifecycle email and recovery flows that help WooCommerce merchants win back abandoned carts and repeat buyers.",
    description:
      "Product design and UX for SkyVerge and Jilt — ecommerce tooling and lifecycle marketing for WooCommerce merchants.",
    featuredMedia: {
      type: "image",
      url: "/assets/work/skyverge-jilt.png",
    },
    color: "#4A90E2",
    client: {
      name: "SkyVerge",
      logo: "/assets/logos/jilt.png",
    },
    content: (
      <>
        <p>
          Township worked with SkyVerge on product design for Jilt, their
          WooCommerce-focused lifecycle marketing tool. The work spanned core
          merchant workflows — campaign setup, recovery messaging, and the
          admin experience merchants live in every day — with UX tuned for
          clarity inside a crowded ecommerce tooling landscape.
        </p>
      </>
    ),
    categories: ["coming-soon"],
  },
  {
    id: "squadlocker",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "SquadLocker",
    description:
      "Streamlined, mobile-responsive Store Builder for custom apparel — intuitive bulk logo management across products, colors, and categories.",
    spotlightCopy: [
      "Township partnered with SquadLocker to design a streamlined, mobile-responsive custom apparel Store Builder for both customers and SquadLocker\u2019s internal team managing store setup and support.",
      "We transformed a complex, existing bulk logo management process into a more intuitive system, allowing users to apply and swap logos across products, colors, and categories at scale with far less friction.",
      "The result was a faster, more flexible setup experience designed to reduce manual support overhead and help users move from setup to store launch with fewer steps and far fewer clicks.",
    ],
    featuredMedia: {
      type: "image",
      url: "/assets/work/squadlocker.png",
    },
    color: "#E85D04",
    client: {
      name: "SquadLocker",
      logo: "/assets/logos/squadlocker.png",
      website: "https://www.squadlocker.com",
    },
    content: (
      <>
        <p>
          Township partnered with SquadLocker to design a streamlined,
          mobile-responsive custom apparel Store Builder for both customers and
          SquadLocker&rsquo;s internal team managing store setup and support.
        </p>
        <p>
          We transformed a complex, existing bulk logo management process into
          a more intuitive system, allowing users to apply and swap logos across
          products, colors, and categories at scale with far less friction.
        </p>
        <p>
          The result was a faster, more flexible setup experience designed to
          reduce manual support overhead and help users move from setup to store
          launch with fewer steps and far fewer clicks.
        </p>
      </>
    ),
    categories: ["digital-experiences"],
  },
  {
    id: "streamlined",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "Streamlined",
    description:
      "VC brand refresh, marketing website, investor deck, and newsletter design and build for a new streamlined minimalist identity.",
    spotlightCopy: [
      "Township partnered with Streamlined, a venture capital firm, on a full rebrand toward a minimalist aesthetic built to match the name: clear, restrained, and streamlined. We designed and built their new marketing website, investor deck, and newsletter templates so the identity holds together across every touchpoint.",
      "Township handled design and development end to end, giving Streamlined a cohesive digital presence with the same precision and clarity as the brand itself.",
    ],
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("streamlined"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/streamlined.png",
    },
    color: "#038AFD",
    client: {
      name: "Streamlined",
      logo: "/assets/logos/streamlined.png",
      website: "https://www.streamlined.vc",
    },
    content: (
      <>
        <p>
          Township partnered with Streamlined, a venture capital firm, on a
          full rebrand toward a minimalist aesthetic built to match the name:
          clear, restrained, and streamlined. We designed and built their new
          marketing website, investor deck, and newsletter templates.
        </p>
        <p>
          Township handled design and development end to end, delivering a
          cohesive digital presence with the same precision and clarity as the
          brand itself.
        </p>
      </>
    ),
    categories: ["digital-experiences", "marketing-websites"],
  },
  {
    id: "ebay-close5",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "Close5",
    description:
      "Mobile product design for Close5, eBay\u2019s local resale app, plus data analysis, listing quality, and growth support.",
    spotlightCopy: [
      "Township partnered with eBay on Close5, the company\u2019s local resale app for discovering, negotiating, and completing nearby buys and sells. Our work spanned mobile product design end to end: discovery and search, listing creation, offers and chat, onboarding, trust cues, and the flows that make a local transaction feel clear on a phone.",
      "We went deeper than screens. Township analyzed Close5 usage data and growth trends to guide product priorities, and helped the team improve listing quality at scale, including image recognition to surface and filter out bad photos before they reached buyers.",
      "We also supported advertising and growth initiatives, giving eBay a design and product partner who could move from UX details to the broader work of building a local marketplace people actually use.",
    ],
    featuredMedia: {
      type: "image",
      url: "/assets/work/ebay-close5.png",
    },
    color: "#E53238",
    client: {
      name: "eBay",
      logo: "/assets/logos/ebay.png",
      website: "https://www.ebay.com",
    },
    content: (
      <>
        <p>
          Township partnered with eBay on Close5, a mobile marketplace for
          local resale. The design work covered discovery, listings, offer and
          chat flows, and the end-to-end experience of buying and selling with
          people nearby — including onboarding, trust cues, and the product
          moments that make a local transaction feel straightforward on a phone.
        </p>
      </>
    ),
    categories: ["mobile-apps", "product-design", "connected-systems"],
  },
  {
    id: "fca",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "FCA",
    description:
      "Marketing website for FCA (Frances Cauffman Architects), following a Pentagram-led rebrand and relaunch.",
    spotlightCopy:
      "Township partnered with Pentagram on the rebrand and relaunch of Frances Cauffman Architects, now FCA. Following the new identity and naming work led by Pentagram, Township designed and developed a new portfolio website for the established architecture firm, translating the refreshed brand into a coherent digital showcase of the studio\u2019s work, expertise, and evolving practice.",
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("fca"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/fca.png",
    },
    color: "#2D3748",
    client: {
      name: "FCA",
      logo: "/assets/logos/fca.png",
      website: "https://www.fcarchitects.com",
    },
    content: (
      <>
        <p>
          Township partnered with Pentagram on the rebrand and relaunch of
          Frances Cauffman Architects, now FCA. Following the new identity and
          naming work led by Pentagram, Township designed and developed a new
          portfolio website for the established architecture firm, translating
          the refreshed brand into a coherent digital showcase of the
          studio&rsquo;s work, expertise, and evolving practice.
        </p>
      </>
    ),
    categories: [
      "digital-experiences",
      "marketing-websites",
      "real-estate-architecture",
    ],
  },
  {
    id: "the-six",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "The Six",
    description:
      "Marketing website for a boutique Midtown office tower with interactive floor plans and a custom availability system for full-floor leasing.",
    spotlightCopy: [
      "Township partnered with Pentagram and the team behind The Six on the launch of a custom website for the boutique Midtown office tower. Designed to market bespoke full-floor office opportunities ranging from approximately 2,500 to 4,500 square feet, the site combines refined branding, interactive floor plans, and a custom availability system that allows agents and prospective tenants to explore and manage floor-by-floor leasing opportunities directly within the platform.",
      "Agents and prospective tenants can build their own custom floor plans directly within the site using Township\u2019s interactive floor plan builder. Designed to work right inside the leasing experience, the tool allows users to customize layouts, explore availability, and visualize their future office space in real time.",
    ],
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("the-six"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/the-six.png",
    },
    color: "#1A1A1A",
    client: {
      name: "The Six",
      logo: "/assets/logos/the-six.png",
    },
    content: (
      <>
        <p>
          Township partnered with Pentagram and the team behind The Six on the
          launch of a custom website for the boutique Midtown office tower.
          Designed to market bespoke full-floor office opportunities ranging
          from approximately 2,500 to 4,500 square feet, the site combines
          refined branding, interactive floor plans, and a custom availability
          system that allows agents and prospective tenants to explore and
          manage floor-by-floor leasing opportunities directly within the
          platform.
        </p>
        <p>
          Agents and prospective tenants can build their own custom floor plans
          directly within the site using Township&rsquo;s interactive floor plan
          builder. Designed to work right inside the leasing experience, the tool
          allows users to customize layouts, explore availability, and visualize
          their future office space in real time.
        </p>
      </>
    ),
    categories: [
      "digital-experiences",
      "marketing-websites",
      "real-estate-architecture",
      "wizard",
    ],
  },
  {
    id: "inolex-product-selector",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "Inolex Product Selector",
    description:
      "Marketing website tool for cosmetic formulators — SafeMate and LuxMate guided ingredient finders with personalized recommendations at selector.inolex.com.",
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("inolex-product-selector"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/inolex-product-selector.png",
    },
    color: "#0B0B0B",
    client: {
      name: "Inolex",
      logo: "/assets/logos/inolex.png",
      website: "https://selector.inolex.com",
    },
    content: (
      <>
        <p>
          Township partnered with Inolex to design and build interactive product
          selector tools on Inolex&rsquo;s marketing website that help formulators
          discover the right preservation and silicone-alternative ingredients
          through guided, step-by-step flows and tailored results.
        </p>
      </>
    ),
    categories: [
      "digital-experiences",
      "marketing-websites",
      "product-design",
      "wizard",
    ],
  },
  {
    id: "inolex-website",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "Inolex Website",
    description:
      "Corporate marketing website for Inolex — sustainable ingredients for health, beauty, and wellness — with a refreshed brand experience and product portfolio.",
    spotlightCopy: [
      "Township was approached by Pentagram to lead the design and rebuild of the Inolex website as part of a comprehensive rebranding initiative.",
      "Township implemented a fresh, innovative frontend interface for Inolex.com, creating a seamless user experience that aligns with the company\u2019s values and sustainability focus.",
      "Later, Township collaborated with Inolex to develop the Product Selector Tool, a wizard-like feature that guides users through ingredient requirements to help them find the perfect Inolex product for their needs.",
    ],
    featuredMedia: {
      type: "image",
      url: "/assets/work/inolex-website.png",
    },
    color: "#0B0B0B",
    client: {
      name: "Inolex",
      logo: "/assets/logos/inolex.png",
      website: "https://www.inolex.com",
    },
    content: (
      <>
        <p>
          Township was approached by Pentagram to lead the design and rebuild of
          the Inolex website as part of a comprehensive rebranding initiative.
        </p>
        <p>
          Township implemented a fresh, innovative frontend interface for
          Inolex.com, creating a seamless user experience that aligns with the
          company&rsquo;s values and sustainability focus.
        </p>
        <p>
          Later, Township collaborated with Inolex to develop the Product
          Selector Tool, a wizard-like feature that guides users through
          ingredient requirements to help them find the perfect Inolex product
          for their needs.
        </p>
      </>
    ),
    categories: ["digital-experiences", "marketing-websites"],
  },
  {
    id: "port-jefferson-ferry",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "case-study",
    title: "Port Jefferson Ferry System",
    description:
      "Operations dashboard and integrated customer ticketing for the Port Jefferson Ferry \u2014 replacing pen-and-paper scheduling with reservations and ticket sales data.",
    spotlightCopy:
      "Township worked to overhaul the Port Jefferson Ferry\u2019s pen-and-paper method of schedule creation with an Operations Dashboard that allows their admins to manage schedules, customer reservations, and collect data on ticket sales. We also designed a new ticketing flow for customers that integrates with the Dashboard, keeping it up to date with the latest scheduling information.",
    featuredMedia: {
      type: "image",
      url: "/assets/work/port-jefferson-ferry.png",
    },
    color: "#1E3A5F",
    client: {
      name: "Port Jefferson Ferry System",
      logo: "/assets/logos/port-jefferson-ferry.png",
      website: "https://www.bportferry.com",
    },
    content: (
      <>
        <p>
          Township worked to overhaul the Port Jefferson Ferry&rsquo;s
          pen-and-paper method of schedule creation with an Operations Dashboard
          that allows their admins to manage schedules, customer reservations,
          and collect data on ticket sales. We also designed a new ticketing
          flow for customers that integrates with the Dashboard, keeping it up
          to date with the latest scheduling information.
        </p>
      </>
    ),
    categories: ["coming-soon"],
  },
  {
    id: "mcallister-towing",
    slug: null,
    listed: false,
    campaignLpOnly: true,
    publicationStatus: "api-only",
    portfolioTier: "architectural",
    title: "McAllister Towing",
    description:
      "Placeholder — McAllister Towing portfolio copy coming soon.",
    spotlightCopy:
      "Placeholder — McAllister Towing spotlight and case study copy coming soon.",
    featuredMedia: {
      type: "video",
      url: workVideoPreviewPath("mcallister-towing"),
    },
    screenshot: {
      type: "image",
      url: "/assets/work/mcallister-towing.png",
    },
    color: "#0C4A6E",
    client: {
      name: "McAllister Towing",
      logo: "/assets/logos/mcallister-towing.png",
    },
    content: (
      <>
        <p>
          Placeholder — McAllister Towing case study copy coming soon.
        </p>
      </>
    ),
    categories: ["coming-soon"],
  },
];
